import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { classes } from "../../nucleo/classes";

export interface PropsDaTabela extends HTMLAttributes<HTMLTableElement> {
  /** Resumo do conteúdo, obrigatório: uma tabela sem legenda não se explica no leitor de tela. */
  legenda: string;
  /** Mostra a legenda na tela, além de anunciá-la. */
  legendaVisivel?: boolean;
  children: ReactNode;
  className?: string;
}

/* O resto das props vai para a `<table>`, como já acontece em `Linha`, `Celula` e
   `CelulaCabecalho`. Faltava só aqui, e o silêncio era a parte ruim: `data-testid` na tabela era
   aceito pelo tipo, descartado na renderização e não reclamava em lugar nenhum — o atributo
   simplesmente não existia no DOM. */
export function Tabela({
  legenda,
  legendaVisivel = false,
  children,
  className,
  ...resto
}: PropsDaTabela) {
  return (
    // A rolagem horizontal fica presa nesta caixa: a página em si nunca rola de lado.
    <div className="tabela-rolagem">
      <table className={classes("tabela", className)} {...resto}>
        <caption className={classes("text-left", legendaVisivel ? "lead" : "sr-only")}>
          {legenda}
        </caption>
        {children}
      </table>
    </div>
  );
}

export function Cabecalho({ children }: { children: ReactNode }) {
  return <thead>{children}</thead>;
}

export function Corpo({ children }: { children: ReactNode }) {
  return <tbody>{children}</tbody>;
}

/* O resto das props vai para o `<tr>`, como já acontece em `Celula` e `CelulaCabecalho`. É o que
   permite a uma linha carregar o próprio estado — `data-testid`, `data-modo` —, e sem isso o
   estado de uma linha só existiria dentro das células dela, onde nem teste nem leitor de tela
   conseguem lê-lo como propriedade da linha. */
export function Linha({
  children,
  className,
  ...resto
}: HTMLAttributes<HTMLTableRowElement> & { children?: ReactNode }) {
  return (
    <tr className={className} {...resto}>
      {children}
    </tr>
  );
}

export function CelulaCabecalho({
  children,
  className,
  ...resto
}: ThHTMLAttributes<HTMLTableCellElement> & { children?: ReactNode }) {
  return (
    <th scope="col" className={className} {...resto}>
      {children}
    </th>
  );
}

export function Celula({
  children,
  className,
  ...resto
}: TdHTMLAttributes<HTMLTableCellElement> & { children?: ReactNode }) {
  return (
    <td className={className} {...resto}>
      {children}
    </td>
  );
}
